import { styled } from "goober";
import { useEffect, useRef, useState } from "react";
import { queries } from "../../shared/queries";
import { useQuery } from "../hooks/useQuery";

// Generic prompts for users without connectors
const genericPrompts = [
  "Monitor GitHub for new issues labeled 'bug', triage them by severity, assign to the on-call engineer, and post a summary to the #incidents Slack channel.",
  "Collect daily standups from Slack, extract blockers and action items, summarize key discussions, and post a digest to the team's Notion workspace.",
  "Track new Jira tickets in the support queue, enrich them with customer context from our CRM, and post priority alerts to Microsoft Teams.",
  "Aggregate customer feedback from Intercom conversations, identify recurring themes and pain points, and generate a weekly insights report for the product team.",
  "Sync new Salesforce leads with HubSpot, enrich contact data with LinkedIn profiles, and notify the sales team in Slack when high-value prospects come in.",
  "Monitor Datadog for critical alerts, automatically create PagerDuty incidents, gather relevant logs, and open a ServiceNow ticket for tracking.",
  "Fetch new Stripe subscription changes, update our Airtable customer database, calculate MRR impact, and send a daily revenue summary to the finance team.",
  "Watch for new Linear issues in the backlog, auto-assign based on component labels, estimate complexity, and keep the Confluence roadmap in sync.",
  "When a new customer signs up in Stripe, enrich their profile with company data, add them to our onboarding Airtable, and notify the CS team in Slack.",
  "Pull open support tickets from Zendesk each morning, prioritize by sentiment and SLA, and post a ranked list to the #support Slack channel.",
  "Watch for merged GitHub PRs, generate release notes from commit messages, update the Confluence changelog, and notify stakeholders in Teams.",
  "Track Salesforce deals closing this quarter, calculate forecast accuracy against targets, and send a weekly pipeline report to the revenue team.",
  "Monitor our uptime endpoint every 5 minutes, and when it goes down, page the on-call engineer, create a status page incident, and post updates to #incidents.",
  "When a new employee is added in our HR system, provision their Okta account, add them to the right Slack channels, and create onboarding tasks in Asana.",
  "Collect NPS survey responses from Intercom, segment by plan tier, analyze trends over time, and generate a monthly report for the leadership team.",
  "Watch for customer churn signals in Stripe — downgrades, failed payments, and cancellations — then alert the retention team in Slack with account context.",
  "Aggregate deploy logs from GitHub Actions, track deploy frequency and failure rates by team, and post weekly engineering metrics to Notion.",
  "Monitor Slack for mentions of competitor names, extract the context and sentiment, and add entries to our competitive intelligence Airtable.",
  "When a PagerDuty incident is resolved, gather the timeline, pull related Datadog metrics, draft a post-mortem, and file it in Confluence.",
  "Track new feature requests across Intercom, Zendesk, and Slack, deduplicate them, score by frequency, and sync the ranked list to a Linear project.",
  "Pull daily e-commerce orders from Stripe, reconcile with inventory in Airtable, flag low-stock items, and send a restocking alert to the ops team.",
  "Watch Google Calendar for cancelled meetings with prospects, update the Salesforce opportunity timeline, and suggest reschedule options to the account owner.",
  "Monitor GitHub for stale PRs older than 3 days, nudge reviewers in Slack, and escalate to the team lead if no action is taken within 24 hours.",
  "Collect weekly time-tracking data from Asana, calculate utilization rates by team member, and post a summary to the #operations Slack channel.",
];

// App-specific prompts keyed by app identifier
const appPrompts: Record<string, string[]> = {
  slack: [
    "Collect daily updates from Slack channels, extract action items and blockers, summarize key discussions, and post a digest to our team Notion workspace.",
    "Monitor Slack for customer support requests in #help, automatically create Zendesk tickets, and notify the support team with relevant context.",
  ],
  github: [
    "Track new GitHub issues labeled 'bug', triage them by severity based on the description, assign to the on-call engineer, and post updates to #incidents.",
    "Monitor GitHub PRs for merge conflicts or failed checks, notify authors in Slack with details, and add a comment suggesting next steps.",
  ],
  jira: [
    "Watch for new Jira tickets in the support queue, enrich them with customer context from Salesforce, calculate priority score, and post summaries to Teams.",
    "Sync Jira sprint progress daily, calculate velocity metrics, identify at-risk items, and generate a weekly status report for stakeholders.",
  ],
  linear: [
    "Watch for new Linear issues, auto-assign based on component labels, estimate complexity from the description, and keep our Confluence roadmap in sync.",
    "Generate weekly Linear sprint summaries with completed items, cycle time metrics, and blockers, then post to the #engineering Slack channel.",
  ],
  notion: [
    "Sync meeting notes from Notion after each standup, extract action items and owners, create follow-up tasks, and send reminders before the next meeting.",
    "Aggregate updates from Notion project databases, identify stale items, calculate progress metrics, and create a daily summary for the team.",
  ],
  intercom: [
    "Collect customer feedback from Intercom conversations, analyze sentiment, identify recurring themes and pain points, and generate weekly insights for product.",
    "Monitor Intercom for conversations mentioning billing issues, escalate to the finance team automatically, and track resolution time.",
  ],
  salesforce: [
    "Track new Salesforce opportunities, enrich contact data with LinkedIn profiles, calculate deal scores, and notify the sales team when high-value prospects come in.",
    "Sync Salesforce closed-won deals to our billing system, update customer success records, and trigger onboarding workflows automatically.",
  ],
  hubspot: [
    "Monitor new HubSpot deals entering the pipeline, calculate conversion probability, track stage velocity, and post weekly pipeline health updates to Slack.",
    "Track HubSpot form submissions from the pricing page, enrich leads with company data, score them, and create follow-up tasks for sales reps.",
  ],
  zendesk: [
    "Monitor Zendesk tickets, analyze sentiment to prioritize urgent issues, route to the right team based on topic, and track SLA compliance.",
    "Aggregate Zendesk customer feedback weekly, identify common pain points and feature requests, and create a summary report for the product team.",
  ],
  stripe: [
    "Track new Stripe subscriptions and upgrades, calculate MRR impact, update our Airtable customer database, and send a daily revenue summary to finance.",
    "Monitor Stripe for failed payments, trigger a recovery email sequence, notify customer success, and track retry outcomes over 7 days.",
  ],
  airtable: [
    "Sync new entries from our Airtable content calendar, check for missing assets, notify assignees of upcoming deadlines, and update status automatically.",
    "Monitor Airtable inventory levels, calculate reorder points, generate purchase orders when stock is low, and notify the operations team.",
  ],
  datadog: [
    "Monitor Datadog for critical alerts, correlate with recent deployments, gather relevant logs and metrics, and create a PagerDuty incident with full context.",
    "Aggregate Datadog metrics daily, calculate error rates and latency percentiles, identify anomalies, and post an infrastructure health report.",
  ],
  pagerDuty: [
    "Track PagerDuty incidents from alert to resolution, gather timeline and actions taken, generate a post-incident report, and update our runbooks.",
    "Monitor PagerDuty alert patterns, identify noisy alerts, calculate on-call burden metrics, and suggest tuning recommendations weekly.",
  ],
  confluence: [
    "Watch for new Confluence pages in the engineering space, extract key updates and decisions, notify relevant stakeholders, and update the changelog.",
    "Sync Confluence documentation with our codebase changes, identify stale docs, assign owners for updates, and track documentation coverage.",
  ],
  googleSheets: [
    "Monitor our Google Sheets expense tracker for new entries, validate against budget limits, flag anomalies, and generate a weekly spending summary.",
    "Aggregate data from multiple Google Sheets sales reports, normalize formats, calculate regional metrics, and create a consolidated dashboard.",
  ],
  googleCalendar: [
    "Watch Google Calendar for new meetings with external participants, prepare briefing documents with attendee context, and send a pre-meeting summary.",
    "Track calendar events across the team, identify scheduling conflicts, calculate meeting load metrics, and suggest focus time blocks.",
  ],
  figma: [
    "Monitor Figma for new design updates in the product file, notify stakeholders for review, track feedback status, and sync approved designs to Notion.",
    "Track Figma comments and feedback, create Linear issues for design changes, assign to designers, and update the design system changelog.",
  ],
  asana: [
    "Watch for new Asana tasks in the marketing project, prioritize by due date and dependencies, send daily summaries, and flag items at risk of missing deadlines.",
    "Sync Asana project progress with our OKR tracker, calculate completion rates, identify blockers, and generate a weekly status report for leadership.",
  ],
  trello: [
    "Monitor Trello boards for cards moving to 'Review', notify reviewers in Slack with card details, track review time, and move to 'Done' when approved.",
    "Track Trello deadlines across all boards, send reminder notifications 24 hours before due dates, and escalate overdue items to project managers.",
  ],
  microsoftTeams: [
    "Collect updates from Teams channels, extract action items and decisions, summarize key discussions, and post a daily digest to the team SharePoint.",
    "Monitor Teams messages for customer escalations, create ServiceNow tickets automatically, notify the support lead, and track resolution time.",
  ],
  serviceNow: [
    "Track ServiceNow incidents, correlate with Datadog alerts, gather diagnostic data, update ticket status automatically, and notify assignees of SLA deadlines.",
    "Monitor ServiceNow change requests, validate approvals, check for conflicts with the maintenance calendar, and notify stakeholders of upcoming changes.",
  ],
  okta: [
    "Monitor Okta for suspicious login attempts or impossible travel, alert the security team in Slack, create an investigation ticket, and log the event.",
    "Track Okta user provisioning events, sync with our HR system, maintain access audit logs, and generate a monthly compliance report.",
  ],
  snowflake: [
    "Query Snowflake for daily business metrics, calculate trends and anomalies, transform data for the dashboard, and alert on significant changes.",
    "Monitor Snowflake query patterns, identify slow or expensive queries, calculate cost attribution by team, and suggest optimization opportunities.",
  ],
  splunk: [
    "Aggregate Splunk security alerts, correlate events across sources, calculate risk scores, and create incident summaries for the SOC team.",
    "Monitor Splunk for compliance-related events, track access to sensitive data, generate audit trails, and alert on policy violations.",
  ],
  // Dev & DevOps
  gitlab: [
    "Watch for new GitLab merge requests, run a lightweight code review checklist, flag missing tests or documentation, and notify the author in Slack.",
    "Track GitLab pipeline failures across projects, identify flaky tests by failure frequency, and post a weekly reliability report to #engineering.",
  ],
  bitbucket: [
    "Monitor Bitbucket for open pull requests without reviewers, auto-assign based on file ownership, and nudge in Slack if reviews are pending over 24 hours.",
    "Track Bitbucket deployment events, correlate with Jira tickets, and generate release notes with links to resolved issues.",
  ],
  vercel: [
    "Monitor Vercel deployments for preview build failures, extract error logs, notify the PR author in Slack, and suggest common fixes based on the error type.",
    "Track Vercel deployment frequency and build times by project, identify regressions, and post a weekly frontend performance summary.",
  ],
  netlify: [
    "Watch Netlify for failed deploys, pull the build log, identify the breaking change from recent commits, and notify the team in Slack with context.",
    "Monitor Netlify bandwidth and build minute usage, forecast when limits will be reached, and alert the team before overages hit.",
  ],
  sentry: [
    "Monitor Sentry for new error spikes, group by release and affected users, create Linear issues for regressions, and alert the owning team in Slack.",
    "Generate a weekly Sentry digest with top unresolved errors, regression trends, and crash-free session rates, then post to #engineering.",
  ],
  newRelic: [
    "Watch New Relic for latency spikes on key endpoints, correlate with recent deploys, and alert the on-call engineer in Slack with a comparison dashboard link.",
    "Aggregate New Relic performance data weekly, calculate error budgets by service, identify SLO violations, and post a reliability scorecard.",
  ],
  jenkins: [
    "Monitor Jenkins for build failures on the main branch, extract the failure reason from logs, notify the committer in Slack, and track mean time to fix.",
    "Track Jenkins build queue depth and agent utilization, identify bottleneck jobs, and post a weekly CI health report to the platform team.",
  ],
  circleci: [
    "Watch CircleCI for workflows that exceed their usual duration, flag potential infrastructure issues, and notify the team with a link to the slow job.",
    "Track CircleCI credit usage by project and workflow, identify the most expensive pipelines, and send a monthly cost optimization report.",
  ],
  terraformCloud: [
    "Monitor Terraform Cloud for failed plan or apply runs, extract the error details, notify the infrastructure team in Slack, and link to the run log.",
    "Track Terraform Cloud workspace drift detection results, flag resources that have changed outside of Terraform, and create remediation tickets.",
  ],
  aws: [
    "Monitor AWS CloudWatch for billing anomalies, break down cost spikes by service and region, and alert the team in Slack when spending exceeds forecasts.",
    "Track AWS CloudTrail for unauthorized API calls or access denied events, correlate with IAM changes, and notify the security team.",
  ],
  googleCloud: [
    "Monitor Google Cloud billing alerts, break down unexpected cost increases by project and service, and notify the engineering lead with optimization suggestions.",
    "Track Google Cloud audit logs for IAM policy changes, flag overly permissive role grants, and alert the security team in Slack.",
  ],
  // Project management
  clickUp: [
    "Watch for new ClickUp tasks in the sprint, auto-assign based on workload and expertise, set priority from the description, and post a daily standup summary.",
    "Track ClickUp task completion rates by team member, calculate velocity trends, identify bottlenecks, and generate a weekly sprint health report.",
  ],
  mondayDotCom: [
    "Monitor monday.com boards for overdue items, escalate to project managers, calculate schedule risk by project, and post a weekly status digest.",
    "Sync monday.com project timelines with Google Calendar, notify team members of upcoming milestones, and flag dependencies at risk of slipping.",
  ],
  shortcut: [
    "Watch for new Shortcut stories, auto-label based on content, estimate points from the description, and keep the team's Slack channel updated on sprint progress.",
    "Generate weekly Shortcut cycle time reports, identify stories stuck in review, and post actionable insights to the engineering channel.",
  ],
  // Communication
  discord: [
    "Monitor Discord community channels for support questions, create Zendesk tickets for unresolved threads, and notify the support team with context.",
    "Track Discord server activity, identify trending topics and engaged members, and generate a weekly community health report for the community team.",
  ],
  zoom: [
    "After each Zoom meeting, pull the recording transcript, extract action items and decisions, create follow-up tasks in Asana, and share the summary in Slack.",
    "Track Zoom meeting patterns across the team, calculate total meeting hours by person, identify meeting-heavy days, and suggest schedule optimizations.",
  ],
  microsoftOutlook: [
    "Monitor a shared Outlook inbox for customer inquiries, categorize by topic, create Zendesk tickets for support requests, and route sales leads to HubSpot.",
    "Track response times on the shared Outlook inbox, identify unanswered emails older than 4 hours, and escalate to the team lead in Slack.",
  ],
  twilio: [
    "Monitor Twilio for failed SMS deliveries, group by error code and recipient region, alert the engineering team, and track delivery recovery rates.",
    "Track Twilio usage and costs by phone number and message type, identify spikes, and send a weekly communications spending summary to finance.",
  ],
  sendGrid: [
    "Monitor SendGrid for email bounce rate spikes, identify affected campaigns and domains, alert the marketing team, and pause sends if rates exceed thresholds.",
    "Track SendGrid deliverability metrics weekly — open rates, bounce rates, and spam complaints — and generate a sender reputation report.",
  ],
  // Support
  freshdesk: [
    "Monitor Freshdesk for high-priority tickets, analyze sentiment to detect frustrated customers, escalate to senior agents, and track first response times.",
    "Aggregate Freshdesk ticket data weekly, calculate resolution times by category, identify recurring issues, and post a support trends report.",
  ],
  freshService: [
    "Track new Freshservice incidents, auto-assign based on category and asset type, escalate SLA breaches, and post daily IT operations summaries.",
    "Monitor Freshservice change requests, verify approval chains are complete, check for scheduling conflicts, and notify affected teams.",
  ],
  front: [
    "Monitor Front for conversations with no reply after 2 hours, nudge the assigned team member in Slack, and escalate if still unresponded after 4 hours.",
    "Track Front conversation volume and response times by inbox, identify peak hours, and generate staffing recommendations for the support team.",
  ],
  // HR & People
  bambooHr: [
    "Watch BambooHR for new hires starting next week, trigger onboarding workflows, provision accounts in Okta, and notify their manager with a checklist.",
    "Track BambooHR time-off requests, calculate team availability for the upcoming sprint, flag understaffed days, and notify project managers.",
  ],
  gusto: [
    "Monitor Gusto for upcoming payroll deadlines, verify all time entries are submitted, notify managers of missing approvals, and confirm payroll is ready to run.",
    "Track new hires added in Gusto, trigger IT provisioning requests in ServiceNow, and create onboarding task lists in Asana for their managers.",
  ],
  greenhouse: [
    "Watch Greenhouse for candidates advancing to the final interview stage, prepare interviewer briefing docs with resume highlights, and schedule debrief meetings.",
    "Track Greenhouse pipeline metrics by role — time in stage, pass-through rates, and source effectiveness — and send a weekly recruiting health report.",
  ],
  lever: [
    "Monitor Lever for candidates stuck in a stage for more than 5 days, nudge the hiring manager in Slack, and escalate if no action is taken.",
    "Aggregate Lever hiring funnel data weekly, calculate conversion rates by source and role, and post insights to the #recruiting Slack channel.",
  ],
  rippling: [
    "Watch Rippling for employee offboarding events, trigger account deprovisioning in Okta, revoke access tokens, and notify IT to reclaim hardware.",
    "Track Rippling headcount changes by department, calculate growth rates, and send a monthly workforce summary to the leadership team.",
  ],
  workday: [
    "Monitor Workday for approved job requisitions, sync them to our careers page, create intake tasks for the recruiting team, and track time-to-fill metrics.",
    "Track Workday performance review completions, nudge managers with outstanding reviews, and report compliance rates to HR leadership.",
  ],
  // Finance
  quickbooks: [
    "Monitor QuickBooks for new invoices past due, send payment reminders to customers, escalate overdue accounts to the collections team, and track aging trends.",
    "Reconcile QuickBooks transactions daily, flag mismatched entries, categorize uncategorized expenses, and generate a weekly bookkeeping summary.",
  ],
  xero: [
    "Track new Xero invoices, match payments to outstanding invoices, flag discrepancies, and send a daily accounts receivable summary to the finance team.",
    "Monitor Xero bank feeds for unusual transactions, categorize new entries, flag amounts over threshold for review, and post a daily cash flow update.",
  ],
  brex: [
    "Monitor Brex for transactions missing receipts, nudge cardholders in Slack with the transaction details, and escalate to their manager after 48 hours.",
    "Track Brex spending by department and category, flag transactions exceeding budget thresholds, and generate a monthly expense analysis for finance.",
  ],
  ramp: [
    "Watch Ramp for out-of-policy spend, notify the cardholder and their manager in Slack, and track policy violation trends by department.",
    "Aggregate Ramp transaction data monthly, identify duplicate subscriptions across the company, calculate potential savings, and send a report to finance.",
  ],
  // CRM & Marketing
  mailchimp: [
    "Monitor Mailchimp campaigns after send, track open and click rates over 24 hours, compare against benchmarks, and post performance highlights to #marketing.",
    "Watch Mailchimp audience for new subscribers, enrich their profile with company data, segment by industry, and trigger a personalized welcome sequence.",
  ],
  activeCampaign: [
    "Monitor ActiveCampaign for contacts hitting lead scoring thresholds, notify sales reps in Slack with engagement history, and create follow-up tasks.",
    "Track ActiveCampaign automation performance weekly — completion rates, drop-off points, and conversions — and post optimization suggestions to #marketing.",
  ],
  pipedrive: [
    "Watch Pipedrive for deals sitting in a stage for more than a week, nudge the deal owner in Slack, and flag stale deals in the weekly pipeline review.",
    "Track Pipedrive win/loss rates by deal source and stage, calculate average deal velocity, and generate a monthly sales performance report.",
  ],
  // Storage & Docs
  dropbox: [
    "Monitor a shared Dropbox folder for new files from clients, notify the project owner in Slack, log the submission in Airtable, and send a confirmation email.",
    "Track Dropbox storage usage by team folder, identify large or duplicate files, and send a monthly cleanup report to folder owners.",
  ],
  box: [
    "Watch Box for new documents uploaded to the legal review folder, notify the assigned reviewer, track review turnaround times, and escalate overdue reviews.",
    "Monitor Box for files shared externally, verify sharing permissions are appropriate, alert the security team for sensitive content, and log all external shares.",
  ],
  googleDocs: [
    "Watch for new Google Docs added to the product specs folder, notify stakeholders for review, track comments and suggestion resolution, and flag stale drafts.",
    "Monitor Google Docs for comments mentioning action items, extract them, create tasks in Linear, and notify the assignee in Slack.",
  ],
  sharePoint: [
    "Monitor SharePoint document libraries for new uploads, route documents for approval based on metadata, notify reviewers in Teams, and track approval times.",
    "Watch SharePoint for documents nearing their retention review date, notify the document owner, and escalate to compliance if no action is taken.",
  ],
  // E-commerce
  shopify: [
    "Monitor Shopify for new orders, flag high-value purchases for priority fulfillment, update inventory in Airtable, and post a daily sales summary to Slack.",
    "Track Shopify refund requests, analyze reasons by product category, identify quality issues trending upward, and alert the product team.",
  ],
  square: [
    "Track Square daily sales by location, compare against targets, identify underperforming stores, and send a morning performance briefing to regional managers.",
    "Monitor Square for chargebacks, gather transaction details and customer history, create dispute response drafts, and track win rates.",
  ],
  // Security
  crowdStrike: [
    "Monitor CrowdStrike for new threat detections, correlate with affected assets and users, create incident tickets, and alert the SOC team in Slack.",
    "Aggregate CrowdStrike endpoint health data weekly, identify machines with outdated sensors, notify IT for remediation, and track compliance rates.",
  ],
  sentinelOne: [
    "Watch SentinelOne for new threat alerts, auto-isolate affected endpoints, gather forensic data, create incident tickets, and notify the security team.",
    "Track SentinelOne agent deployment coverage, identify unprotected endpoints, create remediation tasks for IT, and report on security posture trends.",
  ],
  wiz: [
    "Monitor Wiz for new critical cloud vulnerabilities, map them to affected services and owners, create Jira tickets with remediation steps, and track fix times.",
    "Generate weekly Wiz cloud security posture reports, highlight new misconfigurations, track remediation progress, and post a scorecard to #security.",
  ],
  // Sign & Legal
  docusign: [
    "Track DocuSign envelopes awaiting signatures, send reminders after 48 hours, escalate to the sender if unsigned after a week, and log completion in Salesforce.",
    "Monitor DocuSign for completed contracts, extract key terms and dates, update the contract tracker in Airtable, and notify the legal team of upcoming renewals.",
  ],
};

const PlaceholderOverlay = styled("div")`
  position: absolute;
  top: 12px;
  left: 12px;
  right: 12px;
  bottom: 36px;
  pointer-events: none;
  color: var(--text-primary);
  opacity: 0.3;
  font-size: 12px;
  font-family: InterVariable;
  line-height: 16px;
  white-space: pre-wrap;
  overflow: hidden;
  -webkit-mask-image: linear-gradient(
    to bottom,
    black calc(100% - 12px),
    transparent
  );
  mask-image: linear-gradient(to bottom, black calc(100% - 12px), transparent);
`;

type Props = {
  visible: boolean;
  onPromptChange?: (prompt: string) => void;
};

export function AnimatedPlaceholder({ visible, onPromptChange }: Props) {
  const [connectors, connectorsDetails] = useQuery(
    queries.connectedAppsIndex()
  );
  const [revealedLength, setRevealedLength] = useState(0);
  const animationRef = useRef<number | null>(null);
  const lastTimeRef = useRef<number>(0);
  const revealedRef = useRef(0);

  // Pick a prompt once connectors have loaded, then lock it in.
  const promptRef = useRef<string | null>(null);
  if (promptRef.current === null && connectorsDetails?.type === "complete") {
    const identifiers = new Set(connectors.map((a) => a.identifier));
    const relevant: string[] = [];
    for (const id of identifiers) {
      const p = appPrompts[id];
      if (p) relevant.push(...p);
    }
    const pool = relevant.length > 0 ? relevant : genericPrompts;
    promptRef.current = pool[Math.floor(Math.random() * pool.length)]!;
  }

  const currentPrompt = promptRef.current;

  // Notify parent of current prompt for tab-to-fill
  useEffect(() => {
    if (currentPrompt) onPromptChange?.(currentPrompt);
  }, [currentPrompt, onPromptChange]);

  // Animate text reveal
  useEffect(() => {
    if (!visible || !currentPrompt) return;

    setRevealedLength(0);
    revealedRef.current = 0;
    lastTimeRef.current = 0;

    const speed = 188; // characters per second (matches chat UI)

    const animate = (time: number) => {
      if (lastTimeRef.current === 0) {
        lastTimeRef.current = time;
      }

      const msPerChar = 1000 / speed;
      const elapsed = time - lastTimeRef.current;
      const charsToAdd = Math.floor(elapsed / msPerChar);

      if (charsToAdd > 0) {
        const newRevealed = Math.min(
          revealedRef.current + charsToAdd,
          currentPrompt.length
        );
        revealedRef.current = newRevealed;
        setRevealedLength(newRevealed);
        lastTimeRef.current = time;
      }

      if (revealedRef.current < currentPrompt.length) {
        animationRef.current = requestAnimationFrame(animate);
      } else {
        animationRef.current = null;
        lastTimeRef.current = 0;
      }
    };

    animationRef.current = requestAnimationFrame(animate);

    return () => {
      if (animationRef.current !== null) {
        cancelAnimationFrame(animationRef.current);
        animationRef.current = null;
      }
    };
  }, [visible, currentPrompt]);

  if (!visible || !currentPrompt) return null;

  return (
    <PlaceholderOverlay>
      {currentPrompt.slice(0, revealedLength)}
    </PlaceholderOverlay>
  );
}
